import { useCallback, useMemo, useState } from 'react';
import { listarProdutos } from '../../api/cadastros-do-erp.js';
import type { FiltroDeProdutos, ProdutoDoErp } from '../../api/cadastros-do-erp.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import { CampoBusca, ContadorRegistros, FiltroSelecao } from '../../componentes/Filtros.js';
import { IconeArmazem } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import { useValorAdiado } from '../movimentacoes/useValorAdiado.js';
import { CodigoDoProduto, DatasDaSincronizacao, SituacaoNoErp } from './partes.js';
import { QrDoProduto } from './QrDoProduto.js';
import {
  OPCOES_DE_SITUACAO_DO_CADASTRO,
  SITUACAO_PADRAO_DO_CADASTRO,
  SITUACOES_DO_CADASTRO,
  valorDoSeletor,
} from './situacao.js';

const COLUNAS: readonly Coluna<ProdutoDoErp>[] = [
  {
    chave: 'produto',
    rotulo: 'Produto',
    largura: '30%',
    celula: (produto) => (
      <CodigoDoProduto
        codPro={produto.codPro}
        codDer={produto.codDer}
        descricaoDerivacao={produto.descricaoDerivacao}
        nome={produto.nome}
      />
    ),
  },
  {
    chave: 'unidade',
    rotulo: 'Unidade',
    largura: '80px',
    celula: (produto) => <span className="mono">{produto.unidadeMedida}</span>,
  },
  {
    chave: 'categoria',
    rotulo: 'Categoria',
    largura: '18%',
    celula: (produto) =>
      produto.categoria === null ? (
        <span className="sincronizacao__discreto">Sem categoria</span>
      ) : (
        <span className="sincronizacao__pilha">
          <span>{produto.categoria.nome}</span>
          <span className="sincronizacao__discreto mono">{produto.categoria.codigo}</span>
        </span>
      ),
  },
  {
    chave: 'lote',
    rotulo: 'Lote',
    largura: '64px',
    celula: (produto) => (
      <span aria-label="Controla lote">{produto.controlaLote ? 'Sim' : 'Não'}</span>
    ),
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    largura: '160px',
    celula: (produto) => <SituacaoNoErp ativo={produto.ativo} excluidoEm={produto.excluidoEm} />,
  },
  {
    chave: 'datas',
    rotulo: 'Datas',
    celula: (produto) => (
      <DatasDaSincronizacao
        atualizadoEm={produto.atualizadoEm}
        sincronizadoEm={produto.sincronizadoEm}
      />
    ),
  },
];

/**
 * A ação de QR por linha só existe para quem imprime etiqueta — a mesma
 * permissão da tela de Etiquetas e do `GET /produtos/qrcode`.
 */
const COLUNA_DO_QR: Coluna<ProdutoDoErp> = {
  chave: 'qr',
  rotulo: 'Etiqueta',
  largura: '120px',
  celula: (produto) => <QrDoProduto produto={produto} />,
};

/** Os produtos (com derivação) como a sincronização os gravou do ERP. */
export function AbaProdutos({
  podeImprimirEtiqueta = false,
}: {
  /** `etiquetas.imprimir`, decidido por quem monta a aba (sem permissão, a ação não existe). */
  readonly podeImprimirEtiqueta?: boolean;
}) {
  const colunas = podeImprimirEtiqueta ? [...COLUNAS, COLUNA_DO_QR] : COLUNAS;
  const [busca, definirBusca] = useState('');
  const [categoria, definirCategoria] = useState('');
  const [situacao, definirSituacao] = useState<string>(SITUACAO_PADRAO_DO_CADASTRO);
  const [pagina, definirPagina] = useState(1);
  const buscaAdiada = useValorAdiado(busca);
  const categoriaAdiada = useValorAdiado(categoria);

  const filtro = useMemo<FiltroDeProdutos>(
    () => ({
      pagina,
      ...seInformado('busca', buscaAdiada),
      ...seInformado('categoria', categoriaAdiada),
      ...seInformado('situacao', valorDoSeletor(situacao, SITUACOES_DO_CADASTRO)),
    }),
    [pagina, buscaAdiada, categoriaAdiada, situacao],
  );

  const buscar = useCallback(() => listarProdutos(filtro), [filtro]);
  const consulta = useConsulta(buscar);

  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <div className="sincronizacao__aba">
      <FaixaDeFiltros rotulo="Filtros dos produtos">
        <CampoBusca
          rotulo="Buscar por código ou nome"
          valor={busca}
          largura={260}
          aoMudar={(valor) => {
            recortar(() => {
              definirBusca(valor);
            });
          }}
        />
        <CampoBusca
          rotulo="Categoria (código)"
          valor={categoria}
          largura={180}
          aoMudar={(valor) => {
            recortar(() => {
              definirCategoria(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Situação"
          valor={situacao}
          opcoes={OPCOES_DE_SITUACAO_DO_CADASTRO}
          aoMudar={(valor) => {
            recortar(() => {
              definirSituacao(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>
            {contagemDoRecorte(consulta.dados.total, 'produto', 'produtos')}
          </ContadorRegistros>
        )}
      </FaixaDeFiltros>

      <Resultado
        consulta={consulta}
        espera="Carregando os produtos…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeArmazem tamanho={22} />}
            titulo="Nenhum produto neste recorte."
            detalhe="Os produtos chegam do ERP pela carga inicial e pela sincronização periódica."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={colunas}
            chaveDe={(produto) => `${produto.codPro}|${produto.codDer}`}
            descricao="Produtos do ERP"
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'produto', 'produtos')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </div>
  );
}
